import { defineStore } from "pinia";

import axios from "axios";

import EmployeeService from "@/api/services/employee.service";
import EmployeePermissionService from "@/api/services/employeePermission.service";
import { applyManagedDepartments } from "@/utils/managedDepartments";

// Persistencia mínima heredada de Vuex: mantenemos user/platform en localStorage
// para no cambiar el comportamiento de login al recargar la aplicación.
const readJsonStorage = (key, fallback = null) => {
  const value = localStorage.getItem(key);
  return value ? JSON.parse(value) : fallback;
};

export const useAuthStore = defineStore("auth", {
  state: () => ({
    platform: "public",
    user: null,
    lang: "es",
    isAuthenticated: false,
    showLoginPopup: false,
  }),
  getters: {
    getUserLogged: (state) => {
      // Durante la migración hay rutas/servicios que pueden leer el usuario
      // antes de que el store se haya hidratado explícitamente.
      const stored = readJsonStorage("user", null);
      return state.user ? state.user : stored;
    },
    getUserLoggedFullName: (state) => {
      return state.user ? `${state.user.name} ${state.user.lastName}` : null;
    },
  },
  actions: {
    initialiseStore() {
      // Hidratación manual en lugar de pinia-plugin-persistedstate para persistir
      // solo auth/plataforma y evitar guardar estado transitorio.
      this.user = readJsonStorage("user", null);
      this.platform = readJsonStorage("platform", "public");
      this.isAuthenticated = !!localStorage.getItem("token");

      // Los permisos granulares se conceden en caliente (sin re-login), pero el
      // usuario queda cacheado en localStorage con los permisos de su última
      // sesión. Refrescamos contra el backend en cada arranque/recarga de la
      // SPA para que un cambio de permisos no requiera cerrar sesión.
      if (this.isAuthenticated && this.user) {
        this.refreshPermissions();
      }
    },
    async refreshPermissions() {
      try {
        const permissionsResponse = await EmployeePermissionService.getMyPermissions();
        if (!this.user) {
          return;
        }
        const refreshedUser = applyManagedDepartments(
          { ...this.user, permissions: permissionsResponse?.data?.permissions || [] },
          permissionsResponse?.data?.managedDepartments,
        );
        localStorage.setItem("user", JSON.stringify(refreshedUser));
        this.user = refreshedUser;
      } catch {
        // Mantiene los permisos cacheados si el backend no responde
      }
    },
    setAuth(payload) {
      this.isAuthenticated = payload;
    },
    setShowLoginPopup(payload) {
      this.showLoginPopup = payload;
    },
    async setUserLogged(user) {
      if (!user) {
        localStorage.setItem("user", JSON.stringify(null));
        this.user = null;
        return;
      }

      try {
        const response = await EmployeeService.getEmployee(user.id);
        const employee = response?.data || {};
        const refreshedUser = { ...user };

        refreshedUser.jobName = employee.job?.name || "";
        refreshedUser.workplace = employee.workplace || null;

        // Permisos granulares (módulo:acción) concedidos vía employee_permission —
        // usados por el router para gatear rutas que ya no dependen de roles Symfony
        // ad-hoc por módulo (ej. facturación).
        try {
          const permissionsResponse = await EmployeePermissionService.getMyPermissions();
          refreshedUser.permissions = permissionsResponse?.data?.permissions || [];
        } catch {
          refreshedUser.permissions = [];
        }

        localStorage.setItem("user", JSON.stringify(refreshedUser));
        this.user = refreshedUser;
      } catch {
        localStorage.setItem("user", JSON.stringify(user));
        this.user = user;
      }
    },
    fetchUserLogged(userId) {
      axios.get(`/api/employee/${userId}`).then((response) => {
        this.setUserLogged(response.results.user);
      });
    },
  },
});
